/**
 * ResizeObserver Error Handler Utility
 * 
 * This utility provides methods to handle ResizeObserver errors that commonly occur
 * in React applications with dynamic content, iframes, or third-party embeds.
 */

/**
 * Suppresses ResizeObserver loop limit exceeded errors
 * This is a common error that occurs when ResizeObserver callbacks trigger
 * layout recalculations that cause further resize observations
 */
export const suppressResizeObserverErrors = () => {
  // Store the original console.error
  const originalError = console.error;
  
  console.error = (...args) => {
    // Check if this is a ResizeObserver error
    if (
      args.length > 0 &&
      typeof args[0] === 'string' &&
      (args[0].includes('ResizeObserver loop limit exceeded') ||
       args[0].includes('ResizeObserver loop completed with undelivered notifications'))
    ) {
      // Suppress the error by not logging it
      return;
    }
    
    // For all other errors, use the original console.error
    originalError.apply(console, args);
  };
};

/**
 * Creates a debounced ResizeObserver that reduces the frequency of callbacks
 * to prevent loop limit exceeded errors
 */
export const createDebouncedResizeObserver = (callback, delay = 16) => {
  let timeoutId = null;
  
  return new ResizeObserver((entries) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      callback(entries);
    }, delay);
  });
};

/**
 * Safe wrapper for ResizeObserver that catches and handles errors
 */
export const createSafeResizeObserver = (callback) => {
  try {
    return new ResizeObserver((entries) => {
      try {
        callback(entries);
      } catch (error) {
        if (!error.message.includes('ResizeObserver loop limit exceeded') &&
            !error.message.includes('ResizeObserver loop completed with undelivered notifications')) {
          console.warn('ResizeObserver callback error:', error);
        }
      }
    });
  } catch (error) {
    console.warn('Failed to create ResizeObserver:', error);
    return null;
  }
};

/**
 * Initialize global error suppression for ResizeObserver errors
 * Call this once in your app's entry point
 */
export const initializeResizeObserverErrorHandling = () => {
  // Suppress ResizeObserver errors globally
  suppressResizeObserverErrors();
  
  // Also handle unhandled promise rejections that might be related
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason &&
      typeof event.reason === 'object' &&
      event.reason.message &&
      (event.reason.message.includes('ResizeObserver loop limit exceeded') ||
       event.reason.message.includes('ResizeObserver loop completed with undelivered notifications'))
    ) {
      event.preventDefault();
    }
  });
  
  // Handle general errors
  window.addEventListener('error', (event) => {
    if (
      event.message &&
      (event.message.includes('ResizeObserver loop limit exceeded') ||
       event.message.includes('ResizeObserver loop completed with undelivered notifications'))
    ) {
      event.preventDefault();
    }
  });
};
